import * as React from 'react'
import { Dispatch, ReactElement, SetStateAction, useMemo } from 'react'
import { InteractionTag, Text } from '@fluentui/react-components'

import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { InvoiceApprovalStatus, UserInvoiceApprovalDto } from './InvoiceApprovalOverview'
import { useFormikContext } from 'formik'
import PersonAvatar from '../../components/PersonAvatar'
import useAxios from '../../hooks/useAxios'
import useUsers from '../../hooks/useUsers'
import { useOverviewStyles } from '../BaseOverview/BaseOverview'
import { getTemplateInterval } from '../../components/Form/Finance/Invoice/InvoiceFile'
import { InvoiceApprovalMutationDto, UserInvoiceApprovalMutationDto, ViewInvoiceApprovalComponentProps } from './ViewInvoiceApproval'
import BaseDisbursementOverview, {
    BaseDisbursementOverviewProps,
    DisbursementFilterType,
    ExtendedDisbursementOverviewDto,
} from '../../v2/pages/Disbursement/BaseDisbursementOverview'

export type ViewInvoiceApprovalDisbursementsProps = ViewInvoiceApprovalComponentProps & {
    setDisbursementsCount: Dispatch<SetStateAction<number>>
    setDisbursements: Dispatch<SetStateAction<ExtendedDisbursementOverviewDto[]>>
    selectedDisbursements: ExtendedDisbursementOverviewDto[]
}

export function DisbursementsHeaderFilterTags(props: BaseDisbursementOverviewProps & { userApprovals: UserInvoiceApprovalMutationDto[] }) {
    const { values, setValues } = useFormikContext<DisbursementFilterType>()
    const overviewStyles = useOverviewStyles()
    const { getUserById } = useUsers()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNewWide), [viewport])

    return (
        <div className={overviewStyles.flexGapXS}>
            {props.userApprovals?.map((item, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === item.userId}
                    onClick={() => setValues({ ...props.defaultFilterValues, users: [item.userId], tag: item.userId })}
                    size={listItemSmall ? 'small' : 'medium'}
                >
                    <PersonAvatar name={getUserById(item.userId)?.fullName} color={'colorful'} size={24} />
                </InteractionTag>
            ))}
        </div>
    )
}

export default function ViewInvoiceApprovalDisbursements(props: ViewInvoiceApprovalDisbursementsProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()

    const defaultFilterValues = useMemo<DisbursementFilterType>(
        () => ({
            from: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).start.toISODate(),
            to: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).end.toISODate(),
            users: [],
            isBilled: props.approvalStatus === InvoiceApprovalStatus.Done,
            files: values.fileIds ? values.fileIds : [],
            fileOwners: [],
            isInvoiceApproval: true,
            invoiceApprovals: values.id ? [values.id] : [],
            attributes: [],
            query: '',
        }),
        [values, props.invoiceTemplate],
    )

    const fixedFilterValues = useMemo<DisbursementFilterType>(
        () => ({
            files: values.fileIds || [],
            isInvoiceApproval: true,
            invoiceApprovals: values.id ? [values.id] : [],
        }),
        [values, props.invoiceTemplate],
    )

    return (
        <div className={props.hiddenClassName ? props.hiddenClassName : undefined}>
            {values.fileIds?.length === 0 && <Text>{translate('invoiceapproval.userapprovals.selectfile')}</Text>}
            {values.fileIds?.length > 0 && (
                <BaseDisbursementOverview
                    baseUrl={`/invoice-approval/${props.approval?.id}/disbursement`}
                    hideFileField
                    hideInvoiceField
                    focusQueryOnRender={false}
                    renderLink={!!values.id}
                    noSticky
                    fileId={values.fileIds?.length === 1 ? values.fileIds[0] : undefined}
                    headerDefaultFilterTags={filterProps => (
                        <DisbursementsHeaderFilterTags
                            {...(filterProps as BaseDisbursementOverviewProps)}
                            baseUrl={`/invoice-approval/${props.approval?.id}/disbursement`}
                            userApprovals={values.userApprovals}
                        />
                    )}
                    fixedFilterValues={fixedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    batchSizeItems={50}
                    onCountItems={(_column, result) => props.setDisbursementsCount(result.total)}
                    onLoadItems={(_column, _columnPages, items) => props.setDisbursements([...items])}
                    alwaysLoadAllItems
                    isItemSelectable={() => props.isApprovalActive}
                    isAllItemsSelectable={() => props.isApprovalActive}
                    isItemSelected={item => props.selectedDisbursements.includes(item)}
                    isAllItemsSelected={items => items.filter(item => !props.selectedDisbursements.includes(item)).length === 0}
                    useItemSelectedColor={false}
                    onItemSelected={(data, item) => {
                        if (data.checked === true) {
                            setFieldValue('disbursements', [...values.disbursements, item.id])
                            if (values.id) {
                                axios
                                    .post<UserInvoiceApprovalDto[]>(`/finance/invoice-approval/${values.id}/disbursement?id=${item.id}`)
                                    .then(response => {
                                        if (!!response?.data) {
                                            setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                        }
                                    })
                            }
                        } else {
                            setFieldValue('disbursements', [...values.disbursements.filter(disbursement => disbursement !== item.id)])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/disbursement?id=${item.id}`)
                            }
                        }
                    }}
                    onAllItemsSelected={(data, items) => {
                        if (data.checked === true) {
                            setFieldValue(
                                'disbursements',
                                items.map(item => item.id),
                            )
                            if (values.id) {
                                axios
                                    .post<UserInvoiceApprovalDto[]>(
                                        `/finance/invoice-approval/${values.id}/disbursement?id=${items.map(item => item.id).join(',')}`,
                                    )
                                    .then(response => {
                                        if (!!response?.data) {
                                            setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                        }
                                    })
                            }
                        } else {
                            setFieldValue('disbursements', [])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/disbursement?id=${items.map(item => item.id).join(',')}`)
                            }
                        }
                    }}
                    selectedItems={props.selectedDisbursements}
                />
            )}
        </div>
    )
}
